<template lang="pug">
.stages-new
    .header-item
        h2.header {{ $t(`${baseTranslationPath}.doc_owner`) }}: {{ getDocumentOwner }}
        .error-warning(v-if="hasError()")
            aiq-tooltip(effect="dark", placement="top", :content="$t(`${baseTranslationPath}.rule_update`, { rule: ruleName})", :show-after="500")
                fa-icon.warning-icon(:icon="['fas', 'circle-exclamation']" class="fa-icon" @click="showErrorLog")
    .src-url-item(v-if="getSrcUrl")
      h2 {{ $t(`${baseTranslationPath}.source_url`) }}:
      span.src-url(@click="openSrcUrl") {{ getSrcUrl }}
    ActorList(v-if="getApprovers.length > 0"
        :ActorList="getApprovers"
        :listType="'approvers'")
    ActorList(v-if="getRejecters.length > 0"
        :ActorList="getRejecters"
        :listType="'rejecters'")
    ActorList(v-if="getSkippers.length > 0"
        :ActorList="getSkippers"
        :listType="'skippers'")
    ActorList(v-if="getAssignees.length > 0"
        :ActorList="getAssignees"
        :showTime="false"
        :listType="'assignees'")
    .action-btn-btn-area
        aiq-button(type="danger"
                  :class="performAction ? 'reject-btn btn' : 'disabled-btn'"
                  :disabled="!performAction"
                  @click="takeAction('reject')")
                  fa-icon(class= "btn-icon" :icon="['fas', 'times']")
                  span {{ $t(`${baseTranslationPath}.reject`) }}
        aiq-button(type="primary"
                  :class="performAction ? 'primary-btn btn' : 'disabled-btn'"
                  :disabled="!performAction"
                  @click="takeAction('approve')")
                  fa-icon(class= "btn-icon" :icon="['fas', 'check']")
                  span {{ $t(`${baseTranslationPath}.approve`) }}
        aiq-button(type="primary"
                  :class="performAction ? 'skip-btn btn' : 'disabled-btn'"
                  :disabled="!performAction"
                  @click="takeAction('skip')")
                  fa-icon(class= "btn-icon" :icon="['fas', 'angle-double-right']")
                  span {{ $t(`${baseTranslationPath}.skip`) }}

.reject-modal-editor
    aiq-dialog.modal-editor(:title="$t(`${baseTranslationPath}.reject`)"
        v-model="showRejectModal"
        :show-close="true"
        :close-on-click-modal="true"
        :before-close="handleRejectModalBeforeClose"
        :close-on-press-escape="true"
        @closed="closeEditor")
        .field-container.full-width.is-required
            label {{ $t(`${baseTranslationPath}.reject_reason`) }}
            .el-input
                .custom-rich-text-container
                    rich-text(@update="onUpdateRichTextEditor"
                                :inChat="false")
        .field-container.full-width.reject-btn-group
            aiq-button(class="group-btn" @click="closeEditor") {{ $t('global.cancel') }}
            aiq-button(type="danger" :disabled="!enableRejectBtn" class="group-btn" @click="reject") {{ $t(`${baseTranslationPath}.reject`) }}
</template>
<script>
import get from 'lodash/get';
import ActorList from './ActorList.vue';
import { confirmBeforeDialogClose } from '@/libs';
import RichText from '@/components/RichText/RichText.vue';
import mitt from 'mitt';

const STATUS = {
  APPROVE: 'approve',
  REJECT: 'reject',
  SKIP: 'skip',
};

export default {
  name: 'StagesNew',
  components: {
    ActorList,
    RichText,
  },
  data() {
    return {
      baseTranslationPath:
        'settings_tab.admin_tab.smart_control.control_list_manage',
      rejectReason: {},
      showRejectModal: false,
      richTextEventEmitter: mitt(),
    };
  },
  provide() {
    return {
      richTextEventEmitter: this.richTextEventEmitter,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.richTextEventEmitter.emit('setText', '');
    });
  },
  props: {
    isCurrentStage: {
      type: Boolean,
      default: false,
    },
    stage: {
      type: Object,
      required: true,
    },
    smartControlRecord: {
      type: Object,
      required: true,
    },
  },
  emits: ['act'],
  computed: {
    getDocumentOwner() {
      return get(this.smartControlRecord, 'document.meta.smart_control.owner', 'N/A');
    },
    getSrcUrl() {
      return get(this.smartControlRecord, 'document.meta.smart_control.source_url');
    },
    ruleName() {
      return get(this.stage, 'rule_name', '');
    },
    enableRejectBtn() {
      return get(this.rejectReason, 'text', '').length > 0;
    },
    getApprovers() {
      const actors = get(this.stage, 'actors', []);
      return actors.filter((actor) => actor.status === STATUS.APPROVE);
    },

    getRejecters() {
      const actors = get(this.stage, 'actors', []);
      return actors.filter((actor) => actor.status === STATUS.REJECT);
    },
    getAssignees() {
      return get(this.stage, 'assignees', []);
    },
    getSkippers() {
      const actors = get(this.stage, 'actors', []);
      return actors.filter((actor) => actor.status === STATUS.SKIP);
    },
    canAct() {
      if (!this.isCurrentStage) return false;
      return get(this.smartControlRecord, 'can_act', false);
    },
    performAction() {
      if (!this.isCurrentStage) return false;
      return this.canAct && !this.hasError();
    },
  },
  methods: {
    hasError() {
      const errorLog = get(this.smartControlRecord, 'rule_divergence');
      return !!errorLog;
    },
    openSrcUrl() {
      const rawUrl = this.getSrcUrl;
      if (!rawUrl) return;

      try {
        const url = new URL(rawUrl);
        if (!['http:', 'https:'].includes(url.protocol)) return;

        const newWindow = window.open(url.toString(), '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      } catch {
        // Invalid URL; ignore.
      }
    },
    handleRejectModalBeforeClose() {
      if (!this.rejectReason.text) {
        this.closeEditor();
        return;
      }
      confirmBeforeDialogClose(this, this.closeEditor);
    },
    onUpdateRichTextEditor(msgData) {
      this.rejectReason = msgData;
    },
    showErrorLog() {
      const messageBody = `${this.$t(
        `${this.baseTranslationPath}.error_logs_body`,
      )}`;

      this.$aiq.confirm(
        this.$t(`${this.baseTranslationPath}.error_logs`),
        messageBody,
        {
          cancelButtonText: this.$t('global.cancel'),
        },
      );
    },
    async actionConfirm(title, msg) {
      try {
        await this.$aiq.confirm(
          title,
          msg,
        );
        return true;
      } catch {
        return false;
      }
    },
    async takeAction(action) {
      if (action === 'approve') {
        if (await this.actionConfirm(this.$t(`${this.baseTranslationPath}.approval_confirm.title`),
          this.$t(`${this.baseTranslationPath}.approval_confirm.message`))) {
          this.$emit('act', {
            action: action,
          });
        }
      } else if (action === 'reject') {
        this.showRejectModal = true;
      } else if (action === 'skip') {
        if (await this.actionConfirm(this.$t(`${this.baseTranslationPath}.skip_confirm.title`),
          this.$t(`${this.baseTranslationPath}.skip_confirm.message`))) {
          this.$emit('act', {
            action: action,
          });
        }
      }
    },
    closeEditor() {
      this.showRejectModal = false;
      this.rejectReason = {};
      this.richTextEventEmitter.emit('setText', '');
    },
    reject() {
      this.$emit('act', {
        action: 'reject',
        reason: this.rejectReason.html,
      });
      this.closeEditor();
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';
.stages-new {
    margin-bottom: 50px;
}
.error-warning {
    padding-left: 5px;
}
.fa-icon:focus,
.fa-icon:active {
  outline: none !important;
  box-shadow: none !important;
}
.header-item {
    display: flex;
}
.header {
    font-size: 18px;
    font-weight: 500;
    line-height: 24px
}
.action-btn-btn-area {
    display: flex;
    flex: 1;
    padding-left: 25px;
    padding-right: 5px;
    margin-top: 20px;
    padding-bottom: 10px;
    margin-bottom: auto;
}

.src-url {
  cursor: pointer;
  text-decoration: underline;
  color: $aiq-color-text-selected;
  padding-left: 5px;

}
.src-url-item {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  padding-bottom: 10px;
}
.disabled-btn {
  width: 100%;
  color: $aiq-tab-content-border !important;
  background-color: $aiq-bgc-main-column-list-item-selected !important;
  border: 2px solid $aiq-tab-content-border !important;
}
.btn {
    width: 100%;
    color: $aiq-color-black;
    background-color: $aiq-bgc-main-column-list-item-selected !important;
}

.primary-btn {
    border: 2px solid $aiq-agent-status-available-border !important;
    color: $aiq-agent-status-available-border !important;
    &:hover {
      background-color: $aiq-agent-status-available-border !important;
      color: $aiq-bgc-list-item !important;
    }
}

.reject-btn {
    border: 2px solid $aiq-color-error !important;
    color: $aiq-color-error !important;
    &:hover {
      background-color: $aiq-color-error !important;
      color: $aiq-bgc-list-item !important;
    }
}

.skip-btn {
    border: 2px solid $aiq-text-active !important;
    color: $aiq-text-active !important;
    &:hover {
      background-color: $aiq-text-active !important;
      color: $aiq-bgc-list-item !important;
    }
}

.reject-btn-group {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.warning-icon {
  color: red;
  cursor: pointer;
  margin-left: 10px;
  padding-top:3px;
}

.btn-icon {
  cursor: pointer;
  margin-right: 10px;
}

</style>
